// (C) 2022 GoodData Corporation
/**
 * This is a map from color names to hex color codes.
 * @beta
 */
export const colorNameMap = {
    black: "#000000",
    red: "#ff0000",
    green: "#00aa00",
    blue: "#0000ff",
    white: "#ffffff",
    cyan: "#0092d2",
    magenta: "#cb006b",
    yellow: "#fff10d",
    none: undefined,
};
/**
 * This is the default format string used by the formatter if no format string is specified.
 * @beta
 */
export const DefaultFormat = "#,##0.00";
/**
 * Container for formatting options.
 * @beta
 */
class FormatOptions {
    constructor(includeColors = true, legacyCompatibility = true, thousandsSeparator, decimalSeparator, nanString, positiveInfinity, negativeInfinity) {
        /**
         * Indicate whether the formatting should also include color tags.
         *
         * Default: true
         */
        this.includeColors = true;
        /**
         * Indicates whether the formatter should retain questionable (weird) behavior of the legacy implementation.
         *
         * This is by default turned on.
         *
         * The questionable behavior that is part of the legacy compatibility:
         * ...
         *
         * Default: true
         */
        this.legacyCompatibility = true;
        this.includeColors = includeColors;
        this.legacyCompatibility = legacyCompatibility;
        this.thousandsSeparator = thousandsSeparator;
        this.decimalSeparator = decimalSeparator;
        this.nanString = nanString;
        this.positiveInfinity = positiveInfinity;
        this.negativeInfinity = negativeInfinity;
    }
}
/**
 * Default formatting options:
 * - includeColors=true
 * - legacyCompatibility ON
 * - thousands separation using ','
 * - decimal separation using '.'
 * - NaN values are represented as "NaN"
 * - Positive infinity values are represented as "Infinity"
 * - Negative infinity values are represented as "-Infinity"
 */
FormatOptions.DefaultOptions = new FormatOptions();
export { FormatOptions };
/**
 * This class represents the color information for the formatted value.
 * Mapped from color and custom color and custom bg color. It also fix presents of Hash in color code.
 * Color code is valid hex color code with the '#'.
 *
 * @public
 */
export class ClientColors {
    /**
     * Constructor for ClientColors.
     *
     * @param resultColors - ResultColors object
     */
    constructor(resultColors) {
        if (resultColors.customColor) {
            this.color = resultColors.customColor.startsWith("#")
                ? resultColors.customColor
                : "#" + resultColors.customColor;
        }
        // named color has priority over custom color
        if (resultColors.color) {
            const usedColor = colorNameMap[resultColors.color] ? colorNameMap[resultColors.color] : undefined;
            this.color = usedColor;
        }
        if (resultColors.customBgColor) {
            this.backgroundColor = resultColors.customBgColor.startsWith("#")
                ? resultColors.customBgColor
                : "#" + resultColors.customBgColor;
        }
    }
}
/**
 * Coloring information for the formatted value.
 * @public
 */
export class ResultColors {
    constructor(color, customColor, customBgColor) {
        this.color = color;
        this.customColor = customColor;
        this.customBgColor = customBgColor;
    }
    /**
     * Resets the color object to its initial state.
     */
    reset() {
        this.color = undefined;
        this.customColor = undefined;
        this.customBgColor = undefined;
    }
}
/**
 * Metadata information for the formatted value.
 * @beta
 */
export class ResultMetadata {
    constructor(colors, effectiveFormat, nullConditionFormatter = false) {
        this.colors = colors || new ResultColors();
        this.effectiveFormat = effectiveFormat;
        this.nullConditionFormatter = nullConditionFormatter;
    }
    /**
     * Resets the metadata object to its initial state.
     */
    reset() {
        this.colors.reset();
        this.effectiveFormat = undefined;
        this.nullConditionFormatter = false;
    }
}
//# sourceMappingURL=gooddata_format.js.map